import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import ListPagination from "@/components/ListPagination";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Users, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmDialogContext";

export default function AudiencesPage() {
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [submitting, setSubmitting] = useState(false);

  const fetcher = useCallback((page, limit) => api.get("/audiences", { params: { page, limit } }), []);

  const {
    items: audiences,
    total,
    pages,
    page,
    limit,
    setPage,
    setLimit,
    loading,
    reload,
  } = usePaginatedList(fetcher, []);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSubmitting(true);
    try {
      await api.post("/audiences", form);
      toast.success("Audience created");
      setOpen(false);
      setForm({ name: "", description: "" });
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    const ok = await confirm({
      title: "Delete audience?",
      description: "Delete this audience and all its contacts? This cannot be undone.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/audiences/${id}`);
    toast.success("Audience deleted");
    reload();
  };

  const copyAudience = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.post(`/audiences/${id}/copy`);
      toast.success("Audience copied with all contacts");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Copy failed");
    }
  };

  return (
    <div data-testid="audiences-page">
      <PageHeader
        title="Audience"
        subtitle="Group your contacts into audiences and segments."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm h-11 btn-brand font-semibold" data-testid="new-audience-button">
                <Plus className="h-4 w-4 mr-2" /> New audience
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create audience</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-sm" placeholder="Newsletter subscribers" data-testid="audience-name-input" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider">Description</Label>
                  <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 rounded-sm" rows={3} data-testid="audience-description-input" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={create} disabled={submitting} className="rounded-sm btn-brand font-semibold" data-testid="audience-create-submit">
                  {submitting ? "Creating..." : "Create"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        {loading ? (
          <div className="text-sm text-neutral-500 dark:text-neutral-400">Loading...</div>
        ) : total === 0 ? (
          <div className="text-center py-16">
            <Users className="h-16 w-16 text-neutral-300 dark:text-neutral-600 mx-auto mb-6" />
            <h3 className="font-display font-bold text-2xl mb-2 text-neutral-900 dark:text-neutral-50">Build your first audience</h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 max-w-md mx-auto mb-6">
              Audiences hold the contacts you'll send campaigns to. Start by creating one.
            </p>
            <Button onClick={() => setOpen(true)} className="rounded-sm h-11 btn-brand font-semibold">
              <Plus className="h-4 w-4 mr-2" /> Create audience
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {audiences.map((a) => (
              <Link key={a.id} to={`/audiences/${a.id}`} className="lift-card p-6 rounded-sm block" data-testid={`audience-card-${a.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="h-10 w-10 rounded-sm flex items-center justify-center" style={{ backgroundColor: "var(--bg-muted)" }}>
                    <Users className="h-5 w-5 text-neutral-700 dark:text-neutral-300" />
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => copyAudience(a.id, e)}
                      className="text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors p-1"
                      title="Copy audience"
                      data-testid={`audience-copy-${a.id}`}
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                    <button onClick={(e) => remove(a.id, e)} className="text-neutral-400 hover:text-red-600 transition-colors p-1" data-testid={`audience-delete-${a.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <h3 className="font-display font-bold text-xl tracking-tight mb-1">{a.name}</h3>
                {a.description && <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4 line-clamp-2">{a.description}</p>}
                <div className="pt-4 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-between">
                  <span className="label-overline">Contacts</span>
                  <span className="font-display font-black text-xl">{a.contact_count}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
        {!loading && total > 0 && (
          <ListPagination
            page={page}
            pages={pages}
            total={total}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={setLimit}
            className="mt-4 rounded-sm border border-neutral-200 dark:border-neutral-800"
          />
        )}
      </div>
    </div>
  );
}
